/**
 * 🎯 V183.1: Channel Detection Utility
 * V213.1: Paddle removed - Google Play only on Android
 * 
 * Determines the build channel (web vs Android) and payment provider.
 * Single source of truth for channel-specific logic throughout the app.
 * 
 * Usage:
 * import { IS_WEB, IS_ANDROID, PAYMENT_PROVIDER } from '@/config/channel';
 * 
 * if (IS_WEB) {
 *   // Web-specific code (billing disabled)
 * } else if (IS_ANDROID) {
 *   // Android-specific code (Google Play)
 * }
 */

export type AppChannel = 'web' | 'android' | 'ios';
export type PaymentProvider = 'google_play' | 'apple' | 'none';

/**
 * Current app channel (web or android)
 * Set via VITE_APP_CHANNEL environment variable
 */
export const APP_CHANNEL: AppChannel = 
  (import.meta.env.VITE_APP_CHANNEL as AppChannel) || 'web';

/**
 * Convenience boolean: true if web build
 */
export const IS_WEB = APP_CHANNEL === 'web';

/**
 * Convenience boolean: true if Android build
 */
export const IS_ANDROID = APP_CHANNEL === 'android';

/**
 * Convenience boolean: true if iOS build
 */
export const IS_IOS = APP_CHANNEL === 'ios';

/**
 * Current payment provider (google_play on Android, apple on iOS, none on web)
 * V209.6: Paddle removed - Google Play on Android, Apple on iOS
 */
export const PAYMENT_PROVIDER: PaymentProvider =
  IS_ANDROID ? 'google_play' : IS_IOS ? 'apple' : 'none';

/**
 * Convenience boolean: true if using Google Play
 */
export const IS_GOOGLE_PLAY = PAYMENT_PROVIDER === 'google_play';

/**
 * Convenience boolean: true if using Apple App Store
 */
export const IS_APPLE = PAYMENT_PROVIDER === 'apple';

/**
 * Convenience boolean: true if billing is disabled (web)
 */
export const IS_BILLING_DISABLED = PAYMENT_PROVIDER === 'none';

/**
 * iOS purchase launch gate.
 * Defaults to false until the full iOS billing path is production-verified.
 */
export const IOS_PURCHASE_ENABLED = String(import.meta.env.VITE_IOS_PURCHASE_ENABLED ?? 'false').toLowerCase() === 'true';

// Console log for debugging (DEV only)
if (import.meta.env.DEV) {
  console.log('🎯 V183.1: Channel configuration loaded', {
    channel: APP_CHANNEL,
    provider: PAYMENT_PROVIDER,
    isWeb: IS_WEB,
    isAndroid: IS_ANDROID,
  });
}

// Development-only validation
if (import.meta.env.DEV) {
  const validChannels: AppChannel[] = ['web', 'android', 'ios'];
  const validProviders: PaymentProvider[] = ['google_play', 'apple', 'none'];

  if (!validChannels.includes(APP_CHANNEL)) {
    console.warn(
      `🎯 V183.1: Invalid APP_CHANNEL "${APP_CHANNEL}". Expected: ${validChannels.join(' | ')}`
    );
  }

  if (!validProviders.includes(PAYMENT_PROVIDER)) {
    console.warn(
      `🎯 V183.1: Invalid PAYMENT_PROVIDER "${PAYMENT_PROVIDER}". Expected: ${validProviders.join(' | ')}`
    );
  }

  // Logical validation
  if (IS_WEB && !IS_BILLING_DISABLED) {
    console.warn('🎯 V213.1: Web build has billing disabled');
  }

  if (IS_ANDROID && !IS_GOOGLE_PLAY) {
    console.warn('🎯 V183.1: Android build should use Google Play provider');
  }
}

export default {
  APP_CHANNEL,
  PAYMENT_PROVIDER,
  IS_WEB,
  IS_ANDROID,
  IS_IOS,
  IS_GOOGLE_PLAY,
  IS_APPLE,
  IS_BILLING_DISABLED,
  IOS_PURCHASE_ENABLED,
};

